import { createIdempotencyKey, type StaffPaymentDetailPayload } from "@lafayette/api-client";
import { stateWord } from "@lafayette/domain";
import type { StaffReconcileOutcome } from "@lafayette/schemas";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { AreaLink } from "../../navigation/AreaLink";
import { deepLinks } from "../../navigation/deep-links";
import { areaPath } from "../../navigation/offering";
import {
  emptyNoteClass,
  hintClass,
  linkClass,
  listClass,
  SubHeading,
} from "../../requests/components/parts";
import { formatInstant } from "../../requests/format";
import { Amount } from "../../ui/amount";
import { AsyncState } from "../../ui/async-state";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card } from "../../ui/card";
import { Facts, Fact as RowFact } from "../../ui/facts";
import { Icon } from "../../ui/icon";
import { BackLink } from "../../ui/links";
import { Notice } from "../../ui/notice";
import { PageHeader } from "../../ui/page-header";
import { Panel } from "../../ui/panel";
import { StatusBadge } from "../../ui/status-badge";
import { usePaymentCapabilities } from "../access";
import {
  EvidenceLabel,
  PaymentFailureNotice,
  PaymentStatusBadge,
  StaffOnly,
} from "../components/parts";
import { RefundDialog } from "../components/RefundDialog";
import {
  buildEvidenceTimeline,
  type Money,
  type PaymentDetailModel,
  readPaymentDetail,
} from "../detail-model";
import { describePaymentError, type PaymentFailure } from "../errors";
import { formatMinor, shortFingerprint, shortId } from "../format";
import {
  decisionLabels,
  errorClassLabels,
  exceptionKindLabels,
  label,
  observationKindLabels,
  providerCallKindLabels,
  providerCallStateLabels,
  refundReasonLabels,
  refundStatusLabels,
  resolutionLabels,
  runScopeLabels,
  runStateLabels,
  sourceKindLabels,
  supplierEvidenceLabels,
  transitionEvidenceLabels,
  verificationLabels,
} from "../labels";
import { usePaymentDetail } from "../queries";
import { usePaymentsRuntime } from "../runtime";

// One payment (CHT-759; contract sections 7.3 and 7.4): what it is, where its amount came from, the provider evidence in time order,
// the exceptions, the refunds, the reconcile runs and the history with its actors (the audit trail). Two privileged actions: reconcile
// (ask the provider what happened) and refund. Rules of this page:
//   - Every payment carries FAKE, REAL or NONE. FAKE is never launch evidence.
//   - The page never says "paid" or "refunded" because an action returned: it reads the payment again and shows what the server holds.
//     A refund is "sent to the provider" until the provider confirms it.
//   - A failed reload after an action says so in words, so a stale screen is never mistaken for the truth.
//   - Staff-only data (the history reasons, the amount source) is marked with the staff-only words.

const ACTIVE_FOR_RECONCILE = new Set(["PENDING_PAYMENT", "PAID", "PARTIALLY_REFUNDED", "READY"]);
// A payment that ended can still hold a late capture at the provider: reconcile stays open for it while the provider holds anything for it
// (an order, a call, an observation). Without a provider trail there is nothing to ask.
const ENDED_FOR_RECONCILE = new Set(["EXPIRED", "CANCELLED", "FAILED"]);

export function PaymentDetailPage() {
  const { paymentRequestId = "" } = useParams();
  const { capabilities, isLoading } = usePaymentCapabilities();
  const detail = usePaymentDetail(paymentRequestId, capabilities.read);

  if (isLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.read) {
    return (
      <section className="grid gap-5">
        <BackLink to={areaPath("payments")}>Back to payments</BackLink>
        <AsyncState
          kind="error"
          title="Not available for your role"
          message="Your staff role cannot read a payment. A refund by payment request id is on the Payments page."
        />
      </section>
    );
  }
  if (detail.isPending) {
    return (
      <AsyncState kind="loading" title="Loading the payment" message="Waiting for the answer." />
    );
  }
  if (detail.isError && detail.data === undefined) {
    const failure = describePaymentError(detail.error);
    return (
      <section className="grid gap-5">
        <BackLink to={areaPath("payments")}>Back to payments</BackLink>
        <AsyncState
          kind="error"
          title={
            failure.kind === "forbidden"
              ? "You cannot open this payment"
              : "The payment did not load"
          }
          message={failure.message}
          {...(failure.kind === "forbidden" ? {} : { onRetry: () => void detail.refetch() })}
        />
      </section>
    );
  }
  return (
    <DetailBody
      key={paymentRequestId}
      payload={detail.data as StaffPaymentDetailPayload}
      refreshFailed={detail.isRefetchError}
      refreshing={detail.isFetching}
      onRefresh={() => detail.refetch()}
    />
  );
}

type BodyProps = Readonly<{
  payload: StaffPaymentDetailPayload;
  refreshFailed: boolean;
  refreshing: boolean;
  onRefresh: () => Promise<unknown>;
}>;

function DetailBody({ payload, refreshFailed, refreshing, onRefresh }: BodyProps) {
  const { capabilities } = usePaymentCapabilities();
  const { payments } = usePaymentsRuntime();
  const model = useMemo(() => readPaymentDetail(payload), [payload]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<PaymentFailure | null>(null);
  const [refunding, setRefunding] = useState(false);
  const [lostAccess, setLostAccess] = useState(false);

  const timeline = useMemo(
    () =>
      buildEvidenceTimeline(model, {
        observationKind: (code) => label(observationKindLabels, code),
        decision: (code) => label(decisionLabels, code),
        verification: (code) => label(verificationLabels, code),
        callKind: (code) => label(providerCallKindLabels, code),
        callState: (code) => label(providerCallStateLabels, code),
        errorClass: (code) => label(errorClassLabels, code),
        runScope: (code) => label(runScopeLabels, code),
        runState: (code) => label(runStateLabels, code),
        money: (value) => formatMinor(value?.amountMinor ?? null, value?.currency),
      }),
    [model],
  );

  const status = model.status;
  const canReconcile =
    capabilities.reconcile &&
    status !== null &&
    (ACTIVE_FOR_RECONCILE.has(status) ||
      (ENDED_FOR_RECONCILE.has(status) && model.hasProviderTrail));
  const reconcileReason =
    capabilities.reconcile && !canReconcile
      ? "A payment that ended without a provider order has nothing to reconcile."
      : null;
  const canRefund = capabilities.refund && model.remainingRefundableMinor > 0;
  const refundReason = !capabilities.refund
    ? "Only a supervisor or an administrator can refund."
    : status !== "PAID" && status !== "PARTIALLY_REFUNDED"
      ? "A refund needs a payment that was paid."
      : model.remainingRefundableMinor === 0
        ? model.reservedRefundMinor > 0
          ? "The rest is held by a refund that is waiting for the provider. Reconcile to ask the provider."
          : "Everything was refunded already."
        : null;

  /** Reads the payment again. A failed read is said in words: the screen may be out of date. */
  async function reload(): Promise<boolean> {
    const result = (await onRefresh()) as { isError?: boolean };
    if (result.isError === true) {
      setNotice(null);
      setFailure({
        kind: "network",
        message:
          "The action was sent, but the page could not read the payment again. What you see may be out of date. Reload the page before you act again.",
        retryable: false,
        reload: true,
        quoteCode: null,
      });
      return false;
    }
    return true;
  }

  async function reconcile() {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    setFailure(null);
    try {
      const outcome = await payments.reconcile(model.id, {
        idempotencyKey: createIdempotencyKey(),
      });
      const ok = await reload();
      if (outcome.state === "FAILED") {
        // HTTP 200 with a FAILED run: the provider could not answer. Say so in words; never "Reconcile finished".
        setNotice(null);
        setFailure({
          kind: "unavailable",
          message: describeReconcileFailure(outcome, ok),
          retryable: outcome.errorClass !== "TERMINAL",
          reload: !ok,
          quoteCode: null,
        });
      } else if (ok) {
        setNotice(describeReconcile(outcome));
      }
    } catch (error) {
      const described = describePaymentError(error, "reconcile");
      setFailure(described);
      if (described.kind === "forbidden") setLostAccess(true);
      else if (described.reload) await reload();
    } finally {
      setBusy(false);
    }
  }

  if (lostAccess) {
    return (
      <section className="grid gap-5">
        <Notice tone="warning" role="alert">
          <p>You can no longer open this payment.</p>
        </Notice>
        <div>
          <BackLink to={areaPath("payments")}>Back to payments</BackLink>
        </div>
      </section>
    );
  }

  return (
    <section className="grid gap-5" aria-busy={refreshing}>
      <div>
        <BackLink to={areaPath("payments")}>Back to payments</BackLink>
      </div>
      <PageHeader
        eyebrow={
          <span className="font-bold tracking-[0.14em] text-link">
            {sourceKindLabels[model.sourceType ?? ""]
              ? `${sourceKindLabels[model.sourceType ?? ""]} payment`
              : "Payment"}
          </span>
        }
        title={formatMinor(model.amount?.amountMinor ?? null, model.amount?.currency)}
        titleClassName="sm:text-[44px] sm:leading-[48px]"
        description={
          <>
            <span className="flex flex-wrap items-center gap-1.5 pt-1">
              <PaymentStatusBadge status={status} /> <EvidenceLabel evidence={model.evidence} />{" "}
              {model.openExceptions > 0 ? (
                <Badge tone="warning" className="h-6 py-0 text-[12.5px] font-semibold">
                  {model.openExceptions} open{" "}
                  {model.openExceptions === 1 ? "exception" : "exceptions"}
                </Badge>
              ) : null}
            </span>
            {model.description === null ? null : (
              <span className="block pt-2">{model.description}</span>
            )}
          </>
        }
      />

      {notice === null ? null : (
        <Notice tone="success" role="status">
          <p>{notice}</p>
        </Notice>
      )}
      {failure === null ? null : <PaymentFailureNotice failure={failure} />}
      {refreshFailed && failure === null ? (
        <Notice tone="warning" role="status">
          <p>The last reload failed. This page may be out of date.</p>
          <Button variant="outline" size="sm" onClick={() => void onRefresh()}>
            <Icon name="refresh" />
            Reload now
          </Button>
        </Notice>
      ) : null}

      <Card>
        <fieldset
          className="m-0 flex min-w-0 flex-wrap items-start gap-4 border-0 p-4"
          disabled={busy}
        >
          <legend className="sr-only">Actions on this payment</legend>
          {capabilities.reconcile ? (
            <div className="flex max-w-72 flex-col gap-1">
              <Button
                variant="outline"
                disabled={!canReconcile || busy}
                aria-describedby={reconcileReason === null ? undefined : "payment-reconcile-reason"}
                onClick={() => void reconcile()}
              >
                {busy ? "Asking the provider…" : "Reconcile with the provider"}
              </Button>
              {reconcileReason === null ? (
                <span className={hintClass}>
                  Asks the provider what happened to this payment and records it as evidence. Safe
                  to repeat: nothing is applied twice.
                </span>
              ) : (
                <span id="payment-reconcile-reason" className={hintClass}>
                  {reconcileReason}
                </span>
              )}
            </div>
          ) : null}
          {capabilities.refund ? (
            <div className="flex max-w-72 flex-col gap-1">
              <Button
                variant="outline"
                className="border-destructive-border text-destructive"
                disabled={!canRefund || busy}
                aria-describedby={refundReason === null ? undefined : "payment-refund-reason"}
                onClick={() => {
                  setNotice(null);
                  setFailure(null);
                  setRefunding(true);
                }}
              >
                Refund…
              </Button>
              {refundReason === null ? (
                <span className={hintClass}>
                  Left to refund:{" "}
                  <Amount>
                    {formatMinor(model.remainingRefundableMinor, model.amount?.currency)}
                  </Amount>
                  {model.reservedRefundMinor > 0 ? (
                    <>
                      {" ("}
                      <Amount>
                        {formatMinor(model.reservedRefundMinor, model.amount?.currency)}
                      </Amount>{" "}
                      is waiting for the provider)
                    </>
                  ) : (
                    ""
                  )}
                </span>
              ) : (
                <span id="payment-refund-reason" className={hintClass}>
                  {refundReason}
                </span>
              )}
            </div>
          ) : null}
          {!capabilities.reconcile && !capabilities.refund ? (
            <p className={emptyNoteClass}>Your role can read this payment, not act on it.</p>
          ) : null}
        </fieldset>
      </Card>

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="grid min-w-0 gap-5">
          <EvidenceSection timeline={timeline} evidence={model.evidence} />
          <ExceptionsSection model={model} />
          <RefundsSection model={model} />
          <HistorySection model={model} />
        </div>
        <div className="grid min-w-0 gap-5">
          <SummarySection model={model} />
          <SourceSection model={model} />
        </div>
      </div>

      {refunding ? (
        <RefundDialog
          paymentRequestId={model.id}
          totalMinor={model.amount?.amountMinor ?? null}
          remainingMinor={model.remainingRefundableMinor}
          currency={model.amount?.currency ?? "INR"}
          evidence={model.evidence}
          onDone={(answer) => {
            setRefunding(false);
            void (async () => {
              const ok = await reload();
              if (!ok) return;
              setNotice(
                answer.outcome === "FAILED"
                  ? "The provider did not accept the refund. Nothing was refunded."
                  : answer.outcome === "ALREADY_FINAL"
                    ? "This refund was already final. The page shows what the server holds."
                    : "The refund was sent to the provider. The payment shows as refunded only after the provider confirms it: reconcile to ask.",
              );
            })();
          }}
          onClose={() => setRefunding(false)}
        />
      ) : null}
    </section>
  );
}

/**
 * A reconcile whose run FAILED: the provider could not give an answer. It says the closed error class in words and the counts of what the
 * run did before it stopped (those stay in the evidence), and what to do next. No payload, no provider text.
 */
function describeReconcileFailure(outcome: StaffReconcileOutcome, reloaded: boolean): string {
  const next =
    outcome.errorClass === "TERMINAL"
      ? "Trying again will not help. Ask an engineer to look at it."
      : outcome.errorClass === "AMBIGUOUS"
        ? "The provider may have received the question. Reconcile again before you act on this payment."
        : "Try again in a moment.";
  const counts = `Before it stopped: ${outcome.examined} looked at, ${outcome.applied} applied, ${outcome.duplicates} already known, ${outcome.exceptions} ${outcome.exceptions === 1 ? "exception" : "exceptions"} opened, ${outcome.deferred} deferred.`;
  const stale = reloaded
    ? ""
    : " The page could not read the payment again either: reload the page before you act again.";
  return `The reconcile did not finish: the provider could not answer (the error ${label(errorClassLabels, outcome.errorClass)}). ${counts} ${next}${stale}`;
}

/** What a reconcile did, in words. Counts only: no payload. */
function describeReconcile(outcome: StaffReconcileOutcome): string {
  if (outcome.deferred > 0 && outcome.applied === 0) {
    return `The provider could not give a clear answer for ${outcome.deferred} item${outcome.deferred === 1 ? "" : "s"}. Nothing was changed. Try again later.`;
  }
  const parts = [
    `${outcome.examined} looked at`,
    `${outcome.applied} applied`,
    `${outcome.duplicates} already known`,
    `${outcome.exceptions} ${outcome.exceptions === 1 ? "exception" : "exceptions"} opened`,
  ];
  return `Reconcile finished (${outcome.evidence}): ${parts.join(", ")}.`;
}

function Time({ value }: Readonly<{ value: string | null }>) {
  return <time dateTime={value ?? undefined}>{formatInstant(value)}</time>;
}

const rowBox = "grid gap-1 rounded-sm border border-border p-3 [&_p]:m-0";

function EvidenceSection({
  timeline,
  evidence,
}: Readonly<{ timeline: ReturnType<typeof buildEvidenceTimeline>; evidence: string }>) {
  return (
    <Panel
      id="payment-evidence-title"
      title="Provider evidence"
      description={
        <>
          What the provider said, what Lafayette asked it, and every reconcile, oldest first.{" "}
          {evidence === "FAKE" ? "This is the fake gateway: it is not launch evidence." : null}
        </>
      }
    >
      {timeline.length === 0 ? (
        <p className={emptyNoteClass}>No provider evidence yet.</p>
      ) : (
        <ol className={listClass}>
          {timeline.map((entry) => (
            <li key={entry.key} data-kind={entry.kind} className={rowBox}>
              <strong>{entry.title}</strong>
              <span className={hintClass}>
                <Time value={entry.at} />
              </span>
              {entry.detail === "" ? null : <p className="break-words">{entry.detail}</p>}
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

function ExceptionsSection({ model }: Readonly<{ model: PaymentDetailModel }>) {
  return (
    <Panel id="payment-exceptions-title" title="Exceptions">
      {model.exceptions.length === 0 ? (
        <p className={emptyNoteClass}>No exception for this payment.</p>
      ) : (
        <ul className={listClass}>
          {model.exceptions.map((row, index) => (
            <li key={row.id ?? index} className={rowBox}>
              <p className="flex flex-wrap items-center gap-1.5">
                <strong>{label(exceptionKindLabels, row.kind)}</strong>
                {row.status === "OPEN" ? (
                  <StatusBadge tone="warning">Open</StatusBadge>
                ) : (
                  <StatusBadge tone="success">Resolved</StatusBadge>
                )}
              </p>
              <span className={hintClass}>
                Opened <Time value={row.openedAt} />
                {row.resolvedAt === null ? null : (
                  <>
                    , resolved <Time value={row.resolvedAt} /> (
                    {label(resolutionLabels, row.resolutionCode)})
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function RefundsSection({ model }: Readonly<{ model: PaymentDetailModel }>) {
  const currency = model.amount?.currency;
  return (
    <Panel
      id="payment-refunds-title"
      title="Refunds"
      description={`Refunded so far: ${formatMinor(model.refundedAmountMinor, currency)}. A refund counts only after the provider confirms it.`}
    >
      {model.refunds.length === 0 ? (
        <p className={emptyNoteClass}>No refund was asked for.</p>
      ) : (
        <ul className={listClass}>
          {model.refunds.map((refund) => (
            <li key={refund.id} className={rowBox}>
              <p className="flex flex-wrap items-center gap-1.5">
                <strong>
                  <Amount className="text-xl">
                    {formatMinor(refund.amount?.amountMinor ?? null, refund.amount?.currency)}
                  </Amount>
                </strong>
                <Badge>{label(refundStatusLabels, refund.status)}</Badge>
              </p>
              <span className={hintClass}>
                {label(refundReasonLabels, refund.reasonCode)} · asked{" "}
                <Time value={refund.requestedAt} />
                {refund.settledAt === null ? null : (
                  <>
                    , settled <Time value={refund.settledAt} />
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function HistorySection({ model }: Readonly<{ model: PaymentDetailModel }>) {
  return (
    <Panel id="payment-history-title" title="History">
      <StaffOnly>The reasons staff gave are shown here.</StaffOnly>
      {model.transitions.length === 0 ? (
        <p className={emptyNoteClass}>No history.</p>
      ) : (
        <ol className={listClass}>
          {model.transitions.map((row, index) => (
            <li key={`${row.version ?? index}`} className={rowBox}>
              <strong>
                {row.from === null
                  ? "Created as"
                  : `${stateWord("payment", "staffShort", row.from)} →`}{" "}
                {stateWord("payment", "staffShort", row.to)}
              </strong>
              <span className={hintClass}>
                <Time value={row.occurredAt} /> · {row.actorKind ?? "unknown"} ·{" "}
                {label(transitionEvidenceLabels, row.evidenceKind)}
              </span>
              {row.internalReason === null ? null : (
                <p className="break-words">Reason: {row.internalReason}</p>
              )}
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

function SummarySection({ model }: Readonly<{ model: PaymentDetailModel }>) {
  return (
    <Panel id="payment-summary-title" title="Summary">
      <Facts>
        <Fact term="Payment id" value={model.id} mono />
        <Fact term="Provider" value={model.provider ?? "No order yet"} />
        <Fact term="Provider order" value={model.providerOrderId ?? "—"} mono />
        <Fact term="Created" value={<Time value={model.createdAt} />} />
        <Fact term="Expires" value={<Time value={model.expiresAt} />} />
        <Fact term="Status changed" value={<Time value={model.statusChangedAt} />} />
        {model.paidAt === null ? null : <Fact term="Paid" value={<Time value={model.paidAt} />} />}
        {model.conciergeRequestId === null ? null : (
          <Fact
            term="Request"
            value={
              // An administrator reads payments but holds no Requests area: the offering turns the link into the short id.
              <AreaLink
                area="requests"
                className={linkClass}
                to={deepLinks.request(model.conciergeRequestId)}
                fallback={`Request ${shortId(model.conciergeRequestId)}`}
              >
                Open the request {shortId(model.conciergeRequestId)}
              </AreaLink>
            }
          />
        )}
        {model.retryOfPaymentRequestId === null ? null : (
          <Fact
            term="Replaces"
            value={
              <Link className={linkClass} to={deepLinks.payment(model.retryOfPaymentRequestId)}>
                Open the payment {shortId(model.retryOfPaymentRequestId)}, which this replaces
              </Link>
            }
          />
        )}
      </Facts>
    </Panel>
  );
}

function SourceSection({ model }: Readonly<{ model: PaymentDetailModel }>) {
  const quote = model.quote;
  const source = model.amountSource;
  return (
    <Panel
      id="payment-source-title"
      title="Where the amount came from"
      description="The amount is never typed at payment time. It is copied by the server from a catalog price or an approved quote."
    >
      <StaffOnly />
      {source === null ? (
        <p className={emptyNoteClass}>No amount source is recorded for this payment.</p>
      ) : (
        <Facts>
          <Fact term="Source" value={sourceKindLabels[source.sourceType ?? ""] ?? "—"} />
          <Fact term="Source id" value={source.sourceId ?? "—"} mono />
          <Fact
            term="Source version"
            value={source.sourceVersion === null ? "—" : String(source.sourceVersion)}
          />
          <Fact term="Fingerprint" value={shortFingerprint(source.fingerprint)} mono />
          <Fact term="Resolved" value={<Time value={source.resolvedAt} />} />
        </Facts>
      )}
      {quote === null ? null : (
        <>
          <SubHeading>Quote</SubHeading>
          <Facts>
            <Fact
              term="Amount"
              value={
                <Amount className="text-lg compact:text-base">{formatMoney(quote.amount)}</Amount>
              }
            />
            <Fact term="Valid until" value={<Time value={quote.validUntil} />} />
            <Fact
              term="Evidence kind"
              value={label(supplierEvidenceLabels, quote.supplierEvidenceKind)}
            />
            <Fact term="Customer reads" value={quote.customerTerms ?? "No terms were recorded"} />
          </Facts>
          {model.sourceType === "CONCIERGE_QUOTE" && model.conciergeRequestId !== null ? (
            <p className="m-0">
              <AreaLink
                area="requests"
                className={linkClass}
                to={deepLinks.request(model.conciergeRequestId)}
              >
                Open the request and its quotes
              </AreaLink>
            </p>
          ) : null}
        </>
      )}
    </Panel>
  );
}

const formatMoney = (value: Money | null) =>
  formatMinor(value?.amountMinor ?? null, value?.currency ?? "INR");

function Fact({
  term,
  value,
  mono = false,
}: Readonly<{ term: string; value: React.ReactNode; mono?: boolean }>) {
  return (
    <RowFact term={term}>
      {mono ? <span className="font-mono break-all">{value}</span> : value}
    </RowFact>
  );
}
